import { BarChartOutlined, CloudDownloadOutlined } from '@ant-design/icons'
import type { Chart } from '@antv/g2'
import { useDownload } from '@web/react-common'
import { Button, DatePicker, InputNumber, Typography } from 'antd'
import moment from 'moment'
import React from 'react'

import { useGetFeaturedJournalBarChartQuery } from '@/generated/graphql'

import s from '../kpi.module.less'

export const StatisticsFeaturedJournalBarChart = () => {
  const barDesc = '帖子点击人数'
  const title = '帖子点击人数'
  const divId = 'featuredJournalBarChartDiv'

  const [timeRange, setTimeRange] = React.useState<[moment.Moment, moment.Moment]>([
    moment().subtract(1, 'h'),
    moment(),
  ])
  const [session, setSession] = React.useState<number>(5) // 有记录的精选周刊从第 5 期开始

  const [chart, setChart] = React.useState<Chart>()

  const ref = React.useRef<HTMLDivElement>(null)

  const { data } = useGetFeaturedJournalBarChartQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    variables: {
      startTime: timeRange[0].toLocaleString(),
      endTime: timeRange[1].toLocaleString(),
      session,
    },
  })

  const statistics = data?.statistics.featuredJournalData || []

  const urlFormatter = (url: string) => {
    const regex = /posts\/[0-9]+/
    const res = url.match(regex)
    return res?.[0] || url
  }

  React.useEffect(() => {
    import('../chart.component').then(({ default: Chart }) => {
      const newChart = new Chart({
        container: divId,
        autoFit: true,
        appendPadding: [0, 25, 0, 0],
      })
      newChart.animate(false)
      setChart(newChart)
    })
  }, [])

  React.useEffect(() => {
    if (statistics && chart) {
      chart.clear()
      chart.scale({
        count: {
          range: [0, 1],
        },
        url: {
          alias: barDesc,
          sync: true,
          nice: true,
          formatter: (value: string) => {
            if (!value) {
              return value
            }
            return urlFormatter(value)
          },
        },
        Value: {
          sync: true,
          nice: true,
        },
      })

      chart.axis('count', {
        tickLine: {
          alignTick: true,
        },
      })

      const view = chart.createView()
      view.data(statistics)
      view.interval().position(['url', 'count']).style({ fillOpacity: 1 })

      chart.render()
    }
  }, [chart, statistics])

  const fileName = `Tale 精选周刊点击人数统计图 - ${session} 期 (${timeRange[0].format(
    'YYYY-MM-DD HH:mm'
  )} - ${timeRange[1].format('YYYY-MM-DD HH:mm')}).csv`

  const file = [['帖子链接', '访问人数']].concat(
    statistics.length > 0 ? statistics.map((data) => [urlFormatter(data.url), data.count.toString()]) : [['', '']]
  )

  const header = (title: string) => (
    <div style={{ display: 'flex', fontSize: 32, margin: '16px 0 8px 0', justifyContent: 'center' }}>
      <BarChartOutlined />
      <Typography.Title style={{ marginLeft: 8 }} level={3}>
        {title}
      </Typography.Title>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Button icon={<CloudDownloadOutlined />} type='link' onClick={() => useDownload(file, fileName)}>
          下载
        </Button>
      </div>
    </div>
  )

  return (
    <div className={s.chartContainer}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <DatePicker.RangePicker
          showTime={{ format: 'HH:mm' }}
          format='YYYY-MM-DD HH:mm'
          value={timeRange}
          onChange={(value) => {
            if (value && value[0] && value[1]) {
              setTimeRange([value[0], value[1]])
            }
          }}
        />
        <div style={{ marginLeft: 8, display: 'flex', alignItems: 'center' }}>
          <span style={{ marginRight: 4 }}>{`周期:`}</span>
          <InputNumber precision={0} value={session} onChange={(value) => setSession(Math.round(value))} />
        </div>
      </div>

      {header(title)}
      <div className={s.chartBox}>
        <div id={divId} className={s.chart} ref={ref} />
      </div>
    </div>
  )
}
